import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ExternalLink, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type Review = { id: string; author: string; rating: number; comment: string; date: string };
type ReviewsData = { averageRating: number; totalReviewCount: number; reviews: Review[]; mapsUrl: string; reviewUrl: string };
const mapsUrl = "https://maps.google.com/maps?cid=18326808836228448913";

function RatingStars({ rating }: { rating: number }) {
  return <span className="inline-flex gap-1 text-primary" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, index) => <Star key={index} aria-hidden="true" className={`h-4 w-4 ${index < Math.round(rating) ? "fill-current" : "text-muted-foreground/40"}`} />)}
  </span>;
}

function ReviewCard({ review }: { review: Review }) {
  const [expanded, setExpanded] = useState(false);
  return <article className="flex flex-col rounded-lg border bg-card p-6 min-w-0">
    <RatingStars rating={review.rating} />
    <p className={`mt-4 text-sm leading-relaxed text-card-foreground whitespace-pre-line break-words ${expanded ? "" : "line-clamp-5"}`}>{review.comment || "Rated on Google."}</p>
    {review.comment.length > 220 && <Button variant="link" size="sm" className="self-start px-0" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>{expanded ? "Show less" : "Read more"}</Button>}
    <div className="mt-auto pt-6">
      <p className="font-semibold text-sm break-words">{review.author}</p>
      <p className="text-xs text-muted-foreground mt-1">{new Date(review.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })} · Google review</p>
    </div>
  </article>;
}

export default function GoogleReviewsSection() {
  const [page, setPage] = useState(0);
  const { data, isPending } = useQuery({
    queryKey: ["google-reviews"],
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("google-reviews");
      if (error) throw error;
      return data as ReviewsData;
    },
  });
  const count = data?.reviews.length ?? 0;
  const pages = Math.ceil(count / 3);
  return <section id="reviews" className="py-20 bg-background">
    <div className="container">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
        <div>
          <p className="text-primary-strong font-semibold text-sm uppercase tracking-widest mb-3">Google Reviews</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-primary dark:text-foreground">From our customers</h2>
          {data && <div className="flex flex-wrap items-center gap-3 mt-4">
            <span className="text-2xl font-bold">{data.averageRating.toFixed(1)}</span>
            <RatingStars rating={data.averageRating} />
            <a href={data.mapsUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline underline-offset-4">{data.totalReviewCount} reviews on Google</a>
          </div>}
        </div>
        <Button asChild variant="outline" className="self-start shrink-0"><a href={data?.reviewUrl ?? mapsUrl} target="_blank" rel="noopener noreferrer">{data ? "Leave a review" : "View reviews on Google"}<ExternalLink /></a></Button>
      </div>
      {isPending ? <div className="grid md:grid-cols-3 gap-6" aria-label="Loading Google reviews">{[0, 1, 2].map(i => <div key={i} className="h-64 rounded-lg bg-muted animate-pulse motion-reduce:animate-none" />)}</div> : data && count > 0 ? <>
        <div className="grid md:grid-cols-3 gap-6" aria-live="polite">{data.reviews.slice(page * 3, page * 3 + 3).map(review => <ReviewCard key={review.id} review={review} />)}</div>
        {pages > 1 && <div className="flex items-center justify-end gap-3 mt-6">
          <span className="text-xs text-muted-foreground mr-2">{page + 1} / {pages}</span>
          <Button variant="outline" size="icon" title="Previous reviews" aria-label="Previous reviews" disabled={page === 0} onClick={() => setPage(page - 1)}><ArrowLeft /></Button>
          <Button variant="outline" size="icon" title="Next reviews" aria-label="Next reviews" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}><ArrowRight /></Button>
        </div>}
      </> : <p className="text-muted-foreground text-sm">Read our customer reviews on Google.</p>}
    </div>
  </section>;
}